import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield, Plus, Clock, CheckCircle2, XCircle, AlertTriangle,
  Download, Eye, ChevronDown, ChevronUp, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';

// ─── Types ───────────────────────────────────────────────────────────────────

type TipoDerecho = 'ACCESO' | 'RECTIFICACION' | 'CANCELACION' | 'OPOSICION' | 'REVOCACION_CONSENTIMIENTO';
type EstadoArco  = 'RECIBIDA' | 'EN_PROCESO' | 'RESUELTA' | 'RECHAZADA';

interface SolicitudArco {
  id: string;
  tipoDerecho: TipoDerecho;
  estado: EstadoArco;
  solicitanteNombre: string;
  solicitanteContacto?: string;
  descripcion?: string;
  fechaRecepcion: string;
  fechaLimiteRespuesta: string;
  fechaResolucion?: string;
  motivoRechazo?: string;
  resultadoDetalle?: Record<string, unknown>;
  archivoExportacionUrl?: string;
  archivoExportacionExpiraEn?: string;
  createdAt: string;
}

// ─── Helpers visuales ────────────────────────────────────────────────────────

const TIPO_INFO: Record<TipoDerecho, { label: string; cls: string; descripcion: string }> = {
  ACCESO:                   { label: 'Acceso / Portabilidad', cls: 'bg-blue-50 text-blue-700 border-blue-200',    descripcion: 'El paciente solicita conocer qué datos tiene registrados y recibir una copia de su expediente.' },
  RECTIFICACION:            { label: 'Rectificación',         cls: 'bg-amber-50 text-amber-700 border-amber-200', descripcion: 'El paciente solicita corregir datos incorrectos o incompletos.' },
  CANCELACION:              { label: 'Cancelación',           cls: 'bg-red-50 text-red-700 border-red-200',       descripcion: 'El paciente solicita la eliminación de sus datos identificativos (anonimización). El historial clínico se conserva por ley.' },
  OPOSICION:                { label: 'Oposición',             cls: 'bg-purple-50 text-purple-700 border-purple-200', descripcion: 'El paciente se opone al uso de sus datos para finalidades secundarias (marketing, investigación, etc.).' },
  REVOCACION_CONSENTIMIENTO:{ label: 'Revocación',            cls: 'bg-orange-50 text-orange-700 border-orange-200', descripcion: 'El paciente revoca un consentimiento previamente otorgado.' },
};

const ESTADO_INFO: Record<EstadoArco, { label: string; cls: string; icon: typeof CheckCircle2 }> = {
  RECIBIDA:   { label: 'Recibida',    cls: 'bg-gray-100 text-gray-600',           icon: Clock        },
  EN_PROCESO: { label: 'En proceso',  cls: 'bg-blue-50 text-blue-700',            icon: Loader2      },
  RESUELTA:   { label: 'Resuelta',    cls: 'bg-green-50 text-green-700',          icon: CheckCircle2 },
  RECHAZADA:  { label: 'Rechazada',   cls: 'bg-red-50 text-red-600',              icon: XCircle      },
};

function diasHabilesRestantes(fechaLimite: string): number {
  const hoy   = new Date();
  hoy.setHours(0, 0, 0, 0);
  const limite = new Date(fechaLimite);
  limite.setHours(0, 0, 0, 0);
  let dias = 0;
  const cursor = new Date(hoy);
  while (cursor < limite) {
    cursor.setDate(cursor.getDate() + 1);
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) dias++;
  }
  return dias;
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Countdown badge ─────────────────────────────────────────────────────────

function PlazoBadge({ fechaLimite, estado }: { fechaLimite: string; estado: EstadoArco }) {
  if (estado === 'RESUELTA' || estado === 'RECHAZADA') return null;
  const dias = diasHabilesRestantes(fechaLimite);
  const vencido = dias <= 0;
  const urgente = dias > 0 && dias <= 3;

  return (
    <span className={cn(
      'flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border',
      vencido ? 'bg-red-100 text-red-700 border-red-300 animate-pulse'
        : urgente ? 'bg-amber-100 text-amber-700 border-amber-300'
        : 'bg-gray-100 text-gray-600 border-gray-200',
    )}>
      <Clock size={9} />
      {vencido ? 'VENCIDO' : `${dias}d hábiles`}
    </span>
  );
}

// ─── Formulario nueva solicitud ───────────────────────────────────────────────

interface NuevaSolicitudFormProps {
  pacienteId: string;
  onClose: () => void;
}

function NuevaSolicitudForm({ pacienteId, onClose }: NuevaSolicitudFormProps) {
  const qc = useQueryClient();
  const [tipoDerecho, setTipoDerecho]               = useState<TipoDerecho>('ACCESO');
  const [solicitanteNombre, setSolicitanteNombre]   = useState('');
  const [solicitanteContacto, setSolicitanteContacto] = useState('');
  const [descripcion, setDescripcion]               = useState('');

  const crearMutation = useMutation({
    mutationFn: (data: object) => api.post('/api/arco', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['arco', pacienteId] });
      toast.success('Solicitud ARCO registrada');
      onClose();
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al registrar la solicitud')),
  });

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <p className="font-semibold text-sm text-text-primary">Nueva solicitud ARCO</p>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary">✕</button>
        </div>

        <form
          className="p-5 space-y-4"
          onSubmit={e => {
            e.preventDefault();
            crearMutation.mutate({ pacienteId, tipoDerecho, solicitanteNombre, solicitanteContacto, descripcion });
          }}
        >
          {/* Tipo */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Derecho solicitado *</label>
            <select
              className={inputCls}
              value={tipoDerecho}
              onChange={e => setTipoDerecho(e.target.value as TipoDerecho)}
            >
              {(Object.keys(TIPO_INFO) as TipoDerecho[]).map(t => (
                <option key={t} value={t}>{TIPO_INFO[t].label}</option>
              ))}
            </select>
            <p className="text-xs text-text-secondary mt-1">{TIPO_INFO[tipoDerecho].descripcion}</p>
          </div>

          {tipoDerecho === 'CANCELACION' && (
            <div className="flex items-start gap-2.5 bg-red-50 rounded-xl border border-red-200 p-3">
              <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">
                <strong>Advertencia:</strong> La cancelación anonimiza de forma permanente los datos identificativos del paciente (nombre, CURP, contacto). El historial clínico se conserva desvinculado por obligación legal (NOM-004). Esta acción es <strong>irreversible</strong>.
              </p>
            </div>
          )}

          {/* Nombre del solicitante */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Nombre del solicitante *</label>
            <input
              required
              className={inputCls}
              value={solicitanteNombre}
              onChange={e => setSolicitanteNombre(e.target.value)}
              placeholder="Nombre del paciente o representante"
            />
          </div>

          {/* Contacto */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Medio de contacto / cómo llegó</label>
            <input
              className={inputCls}
              value={solicitanteContacto}
              onChange={e => setSolicitanteContacto(e.target.value)}
              placeholder="Ej. correo: juan@mail.com, solicitud verbal, carta…"
            />
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Descripción / detalle de la solicitud</label>
            <textarea
              rows={3}
              className={inputCls}
              value={descripcion}
              onChange={e => setDescripcion(e.target.value)}
              placeholder="¿Qué quiere exactamente el paciente?"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-200 rounded-lg py-2 text-sm text-text-secondary hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={crearMutation.isPending}
              className="flex-1 bg-primary-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {crearMutation.isPending ? 'Guardando…' : 'Registrar solicitud'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Tarjeta individual ───────────────────────────────────────────────────────

interface CardProps {
  solicitud: SolicitudArco;
  pacienteId: string;
  canEdit: boolean;
}

function SolicitudCard({ solicitud, pacienteId, canEdit }: CardProps) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const tipoInfo   = TIPO_INFO[solicitud.tipoDerecho];
  const estadoInfo = ESTADO_INFO[solicitud.estado];
  const EstadoIcon = estadoInfo.icon;

  const procesarAccesoMutation = useMutation({
    mutationFn: () => api.post(`/api/arco/${solicitud.id}/acceso`).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['arco', pacienteId] }); toast.success('Paquete generado. Descarga disponible.'); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al generar el paquete de exportación')),
  });

  const refrescarUrlMutation = useMutation({
    mutationFn: () => api.post(`/api/arco/${solicitud.id}/refrescar-url`).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['arco', pacienteId] });
      if (data.zipSigned) window.open(data.zipSigned, '_blank');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al generar URL de descarga')),
  });

  const cancelacionMutation = useMutation({
    mutationFn: () => api.post(`/api/arco/${solicitud.id}/cancelacion`).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['arco', pacienteId] }); toast.success('Datos del paciente anonimizados correctamente'); setConfirmCancel(false); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al ejecutar la anonimización')),
  });

  const isPending = procesarAccesoMutation.isPending || refrescarUrlMutation.isPending || cancelacionMutation.isPending;

  return (
    <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 p-4">
        <div className="flex items-start gap-3">
          <span className={cn('flex items-center justify-center w-8 h-8 rounded-lg border flex-shrink-0 mt-0.5', tipoInfo.cls)}>
            <Shield size={14} />
          </span>
          <div>
            <p className="text-sm font-semibold text-text-primary leading-tight">{tipoInfo.label}</p>
            <p className="text-xs text-text-secondary mt-0.5">{solicitud.solicitanteNombre}</p>
            <p className="text-xs text-text-secondary">{formatDate(solicitud.fechaRecepcion)}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <span className={cn('flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full', estadoInfo.cls)}>
            <EstadoIcon size={10} />
            {estadoInfo.label}
          </span>
          <PlazoBadge fechaLimite={solicitud.fechaLimiteRespuesta} estado={solicitud.estado} />
        </div>
      </div>

      {/* Expandible */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-4 pb-3 text-xs text-text-secondary hover:text-text-primary transition-colors"
      >
        <span>Ver detalle</span>
        {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>

      {expanded && (
        <div className="border-t border-gray-50 px-4 pb-4 pt-3 space-y-3">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-text-secondary">Fecha recepción</p>
              <p className="font-medium text-text-primary">{formatDate(solicitud.fechaRecepcion)}</p>
            </div>
            <div>
              <p className="text-text-secondary">Límite legal (20 d.h.)</p>
              <p className="font-medium text-text-primary">{formatDate(solicitud.fechaLimiteRespuesta)}</p>
            </div>
            {solicitud.fechaResolucion && (
              <div>
                <p className="text-text-secondary">Fecha resolución</p>
                <p className="font-medium text-text-primary">{formatDate(solicitud.fechaResolucion)}</p>
              </div>
            )}
            {solicitud.solicitanteContacto && (
              <div className="col-span-2">
                <p className="text-text-secondary">Contacto</p>
                <p className="font-medium text-text-primary">{solicitud.solicitanteContacto}</p>
              </div>
            )}
          </div>

          {solicitud.descripcion && (
            <div className="bg-gray-50 rounded-lg p-3 text-xs text-text-primary">{solicitud.descripcion}</div>
          )}

          {solicitud.motivoRechazo && (
            <div className="bg-red-50 rounded-lg p-3 border border-red-100 text-xs text-red-700">
              <strong>Motivo de rechazo:</strong> {solicitud.motivoRechazo}
            </div>
          )}

          {/* Acciones por tipo */}
          {canEdit && solicitud.estado !== 'RESUELTA' && solicitud.estado !== 'RECHAZADA' && (
            <div className="flex flex-wrap gap-2 pt-1">
              {solicitud.tipoDerecho === 'ACCESO' && solicitud.estado !== 'EN_PROCESO' && (
                <button
                  onClick={() => procesarAccesoMutation.mutate()}
                  disabled={isPending}
                  className="flex items-center gap-1.5 bg-blue-600 text-white rounded-lg px-3 py-1.5 text-xs font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {procesarAccesoMutation.isPending ? <Loader2 size={11} className="animate-spin" /> : <Eye size={11} />}
                  Generar paquete de exportación
                </button>
              )}
              {solicitud.tipoDerecho === 'ACCESO' && solicitud.estado === 'EN_PROCESO' && (
                <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-2 border border-blue-100">
                  <Loader2 size={12} className="animate-spin flex-shrink-0" />
                  Generando PDF y ZIP… se actualizará automáticamente cuando esté listo.
                </div>
              )}

              {solicitud.tipoDerecho === 'CANCELACION' && !confirmCancel && (
                <button
                  onClick={() => setConfirmCancel(true)}
                  className="flex items-center gap-1.5 bg-red-600 text-white rounded-lg px-3 py-1.5 text-xs font-medium hover:bg-red-700 transition-colors"
                >
                  <XCircle size={11} /> Ejecutar anonimización
                </button>
              )}
            </div>
          )}

          {/* Confirmación doble paso para cancelación */}
          {confirmCancel && (
            <div className="bg-red-50 rounded-xl border border-red-200 p-3 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-800">
                  <strong>¿Confirmar anonimización?</strong><br/>
                  Esta acción es <strong>irreversible</strong>. Se eliminarán nombre, CURP, contacto y documentos del paciente. El historial clínico se conservará sin datos de identidad.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmCancel(false)}
                  className="flex-1 border border-gray-200 rounded-lg py-1.5 text-xs text-text-secondary hover:bg-white transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => cancelacionMutation.mutate()}
                  disabled={cancelacionMutation.isPending}
                  className="flex-1 bg-red-600 text-white rounded-lg py-1.5 text-xs font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {cancelacionMutation.isPending ? 'Procesando…' : 'Sí, anonimizar'}
                </button>
              </div>
            </div>
          )}

          {/* Descarga del paquete de acceso */}
          {solicitud.tipoDerecho === 'ACCESO' && solicitud.estado === 'RESUELTA' && (
            <button
              onClick={() => refrescarUrlMutation.mutate()}
              disabled={refrescarUrlMutation.isPending}
              className="flex items-center gap-1.5 border border-blue-200 text-blue-700 bg-blue-50 rounded-lg px-3 py-1.5 text-xs font-medium hover:bg-blue-100 disabled:opacity-50 transition-colors"
            >
              {refrescarUrlMutation.isPending ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
              Descargar expediente (PDF + ZIP)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Tab principal ────────────────────────────────────────────────────────────

interface Props {
  pacienteId: string;
  canEdit: boolean;
}

export function ArcoTab({ pacienteId, canEdit }: Props) {
  const [formOpen, setFormOpen] = useState(false);

  const { data: solicitudes = [], isLoading } = useQuery<SolicitudArco[]>({
    queryKey: ['arco', pacienteId],
    queryFn: () => api.get('/api/arco', { params: { pacienteId } }).then(r => r.data),
    // Polling activo mientras haya solicitudes en proceso (esperando al worker BullMQ)
    refetchInterval: (query) => {
      const data = query.state.data as SolicitudArco[] | undefined;
      return data?.some(s => s.estado === 'EN_PROCESO') ? 4000 : false;
    },
  });

  const vencidas = solicitudes.filter(s =>
    (s.estado === 'RECIBIDA' || s.estado === 'EN_PROCESO') &&
    diasHabilesRestantes(s.fechaLimiteRespuesta) <= 0,
  );

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-text-secondary">
              <span className="font-semibold text-text-primary">{solicitudes.length}</span>{' '}
              solicitud{solicitudes.length !== 1 ? 'es' : ''} ARCO
            </p>
            {vencidas.length > 0 && (
              <p className="text-[10.5px] text-red-600 font-medium mt-0.5 flex items-center gap-1">
                <AlertTriangle size={10} />
                {vencidas.length} solicitud{vencidas.length !== 1 ? 'es' : ''} con plazo vencido
              </p>
            )}
          </div>
          {canEdit && (
            <button
              onClick={() => setFormOpen(true)}
              className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors"
            >
              <Plus size={13} /> Nueva solicitud
            </button>
          )}
        </div>

        {/* Info legal */}
        <div className="bg-blue-50 rounded-xl border border-blue-100 p-3 text-xs text-blue-800 leading-relaxed">
          <strong>LFPDPPP — Plazos legales:</strong> la clínica tiene <strong>20 días hábiles</strong> para responder y <strong>15 días hábiles adicionales</strong> para hacerlo efectivo. El incumplimiento es denunciable ante el <strong>INAI</strong>.
        </div>

        {/* Lista */}
        {isLoading ? (
          <p className="text-center py-10 text-sm text-text-secondary">Cargando…</p>
        ) : solicitudes.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <Shield size={28} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-text-secondary">Sin solicitudes ARCO registradas</p>
            {canEdit && (
              <button
                onClick={() => setFormOpen(true)}
                className="mt-3 text-xs text-primary-600 hover:underline"
              >
                Registrar primera solicitud
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {solicitudes.map(s => (
              <SolicitudCard key={s.id} solicitud={s} pacienteId={pacienteId} canEdit={canEdit} />
            ))}
          </div>
        )}
      </div>

      {formOpen && (
        <NuevaSolicitudForm pacienteId={pacienteId} onClose={() => setFormOpen(false)} />
      )}
    </>
  );
}
